import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Plus, Newspaper, Calendar, Pencil, Trash2, Eye, EyeOff, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const NEWS_CATEGORIES = ['Academic', 'Sports', 'Arts', 'Community', 'Facility', 'General'];
const EVENT_TYPES = ['Academic', 'Sports', 'Arts', 'Admissions', 'Community', 'General'];

export default function NewsEventsManagement() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [showNewsDialog, setShowNewsDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newsForm, setNewsForm] = useState({ title: '', content: '', category: 'General', date: '', is_published: true });
  const [eventForm, setEventForm] = useState({ title: '', date: '', time: '', event_type: 'General', description: '', is_published: true });
  const [newsImageFile, setNewsImageFile] = useState(null);
  const [newsImagePreview, setNewsImagePreview] = useState('');

  const loadAll = () => {
    api.get('/admin/news').then(r => setNews(r.data)).catch(() => {});
    api.get('/admin/events').then(r => setEvents(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  // News CRUD
  const openNewNews = () => {
    setEditingNews(null);
    setNewsForm({ title: '', content: '', category: 'General', date: new Date().toISOString().split('T')[0], is_published: true });
    setNewsImageFile(null);
    setNewsImagePreview('');
    setShowNewsDialog(true);
  };

  const openEditNews = (article) => {
    setEditingNews(article);
    setNewsForm({ title: article.title, content: article.content, category: article.category || 'General', date: article.date || '', is_published: article.is_published !== false });
    setNewsImageFile(null);
    setNewsImagePreview(article.image_url || '');
    setShowNewsDialog(true);
  };

  const saveNews = async () => {
    if (!newsForm.title || !newsForm.content) { toast.error('Title and content are required'); return; }
    try {
      let imageUrl = editingNews?.image_url || '';
      if (newsImageFile) {
        const fd = new FormData();
        fd.append('file', newsImageFile);
        try {
          const { data: fileData } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          imageUrl = fileData.url || fileData.path || '';
        } catch { toast.error('Image upload failed, saving without image'); }
      }
      const payload = { ...newsForm, image_url: imageUrl };
      if (editingNews) {
        await api.put(`/admin/news/${editingNews.id}`, payload);
        toast.success('Article updated');
      } else {
        await api.post('/admin/news', payload);
        toast.success('Article published');
      }
      setShowNewsDialog(false);
      setNewsImageFile(null);
      setNewsImagePreview('');
      loadAll();
    } catch { toast.error('Failed to save article'); }
  };

  const deleteNews = async (id) => {
    if (!window.confirm('Delete this news article?')) return;
    try {
      await api.delete(`/admin/news/${id}`);
      toast.success('Article deleted');
      loadAll();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleNewsPublish = async (article) => {
    try {
      await api.put(`/admin/news/${article.id}`, { is_published: !article.is_published });
      toast.success(article.is_published ? 'Unpublished' : 'Published');
      loadAll();
    } catch { toast.error('Failed to update'); }
  };

  // Events CRUD
  const openNewEvent = () => {
    setEditingEvent(null);
    setEventForm({ title: '', date: '', time: '', event_type: 'General', description: '', is_published: true });
    setShowEventDialog(true);
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({ title: event.title, date: event.date || '', time: event.time || '', event_type: event.event_type || 'General', description: event.description || '', is_published: event.is_published !== false });
    setShowEventDialog(true);
  };

  const saveEvent = async () => {
    if (!eventForm.title || !eventForm.date) { toast.error('Title and date are required'); return; }
    try {
      if (editingEvent) {
        await api.put(`/admin/events/${editingEvent.id}`, eventForm);
        toast.success('Event updated');
      } else {
        await api.post('/admin/events', eventForm);
        toast.success('Event created');
      }
      setShowEventDialog(false);
      loadAll();
    } catch { toast.error('Failed to save event'); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success('Event deleted');
      loadAll();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleEventPublish = async (event) => {
    try {
      await api.put(`/admin/events/${event.id}`, { is_published: !event.is_published });
      toast.success(event.is_published ? 'Unpublished' : 'Published');
      loadAll();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div data-testid="news-events-management">
      <h1 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-2">
        <Newspaper className="w-6 h-6" /> News & Events
      </h1>
      <p className="text-sm text-slate-500 mb-6">Manage articles and events shown on the public website.</p>

      <Tabs defaultValue="news">
        <TabsList className="mb-6">
          <TabsTrigger value="news"><Newspaper className="w-4 h-4 mr-1" /> News Articles</TabsTrigger>
          <TabsTrigger value="events"><Calendar className="w-4 h-4 mr-1" /> Events</TabsTrigger>
        </TabsList>

        {/* News Tab */}
        <TabsContent value="news">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">{news.length} article{news.length !== 1 ? 's' : ''}</p>
            <Button onClick={openNewNews} className="bg-[#1B2A4A]" data-testid="add-news-btn">
              <Plus className="w-4 h-4 mr-1" /> New Article
            </Button>
          </div>
          {news.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-400">No news articles yet. Click "New Article" to publish your first update.</CardContent></Card>
          ) : (
            <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="data-table">
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {news.map(a => (
                    <TableRow key={a.id}>
                      <TableCell>
                        <p className="font-semibold text-[#1B2A4A] text-sm">{a.title}</p>
                        <p className="text-xs text-slate-400 line-clamp-1 max-w-xs">{a.content}</p>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{a.category}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-500">{a.date}</TableCell>
                      <TableCell>
                        <Badge className={a.is_published !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                          {a.is_published !== false ? 'Published' : 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => toggleNewsPublish(a)} title={a.is_published ? 'Unpublish' : 'Publish'}>
                            {a.is_published !== false ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-emerald-600" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditNews(a)} data-testid={`edit-news-${a.id}`}>
                            <Pencil className="w-4 h-4 text-[#1B2A4A]" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteNews(a.id)} data-testid={`delete-news-${a.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">{events.length} event{events.length !== 1 ? 's' : ''}</p>
            <Button onClick={openNewEvent} className="bg-[#1B2A4A]" data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-1" /> New Event
            </Button>
          </div>
          {events.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-400">No events yet. Click "New Event" to add your first school event.</CardContent></Card>
          ) : (
            <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="data-table">
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map(e => (
                    <TableRow key={e.id}>
                      <TableCell>
                        <p className="font-semibold text-[#1B2A4A] text-sm">{e.title}</p>
                        {e.description && <p className="text-xs text-slate-400 line-clamp-1 max-w-xs">{e.description}</p>}
                      </TableCell>
                      <TableCell><Badge className="bg-[#1B2A4A] text-white text-[10px]">{e.event_type}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-500">{e.date}</TableCell>
                      <TableCell className="text-sm text-slate-500">{e.time || '—'}</TableCell>
                      <TableCell>
                        <Badge className={e.is_published !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                          {e.is_published !== false ? 'Published' : 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => toggleEventPublish(e)}>
                            {e.is_published !== false ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-emerald-600" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditEvent(e)} data-testid={`edit-event-${e.id}`}>
                            <Pencil className="w-4 h-4 text-[#1B2A4A]" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteEvent(e.id)} data-testid={`delete-event-${e.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* News Dialog */}
      <Dialog open={showNewsDialog} onOpenChange={setShowNewsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingNews ? 'Edit Article' : 'New Article'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={newsForm.title} onChange={e => setNewsForm({...newsForm, title: e.target.value})} placeholder="Article headline" data-testid="news-title-input" />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea value={newsForm.content} onChange={e => setNewsForm({...newsForm, content: e.target.value})} rows={4} placeholder="Write the article content..." data-testid="news-content-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={newsForm.category} onValueChange={v => setNewsForm({...newsForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{NEWS_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={newsForm.date} onChange={e => setNewsForm({...newsForm, date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Cover Image (optional)</Label>
              <Input
                type="file"
                accept=".jpg,.jpeg,.png"
                onChange={e => {
                  const f = e.target.files[0];
                  if (f) {
                    setNewsImageFile(f);
                    setNewsImagePreview(URL.createObjectURL(f));
                  }
                }}
                className="mt-1"
                data-testid="news-image-input"
              />
              {newsImagePreview && (
                <div className="mt-2 relative">
                  <img src={newsImagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg border" />
                  <button
                    type="button"
                    onClick={() => { setNewsImageFile(null); setNewsImagePreview(''); }}
                    className="absolute top-1 right-1 bg-white/80 rounded-full p-1 hover:bg-white"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewsDialog(false)}>Cancel</Button>
            <Button onClick={saveNews} className="bg-[#1B2A4A]" data-testid="save-news-btn">{editingNews ? 'Update' : 'Publish'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingEvent ? 'Edit Event' : 'New Event'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} placeholder="Event name" data-testid="event-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input type="date" value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})} data-testid="event-date-input" />
              </div>
              <div>
                <Label>Time</Label>
                <Input value={eventForm.time} onChange={e => setEventForm({...eventForm, time: e.target.value})} placeholder="e.g., 09:00 - 13:00" />
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={eventForm.event_type} onValueChange={v => setEventForm({...eventForm, event_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} rows={2} placeholder="Brief event description..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>Cancel</Button>
            <Button onClick={saveEvent} className="bg-[#1B2A4A]" data-testid="save-event-btn">{editingEvent ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
